import { useState, useEffect, useReducer } from 'react';
import styled from 'styled-components';
import { CircularProgress, Switch } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';

import { initialState, categoryReducer } from './CategoryReducer';

const styles = () => ({ progress: { color: '#4DB181' }});

const sortAlphabetical = (a, b) => a > b ? 1 : -1;

export function getUpdate(newState, currentState) {
  const update = {};
  for (const prop of Object.keys(newState)) {
    if (newState[prop] && typeof newState[prop] === 'string') {
      if (newState[prop] !== currentState[prop]) {
        update[prop] = newState[prop];
        continue;
      }
    }

    if (
      typeof newState[prop] === 'boolean' &&
      newState[prop] !== currentState[prop]
    ) {
      update[prop] = newState[prop];
      continue;
    }

    if (newState[prop] && Array.isArray(newState[prop])) {
      if (!currentState[prop] && newState[prop].length === 0) continue;


      if (newState[prop].length !== currentState[prop]?.length) {
        update[prop] = newState[prop];
        continue;
      }

      if (
        JSON.stringify(newState[prop].sort(sortAlphabetical)) !==
        JSON.stringify(currentState[prop].sort(sortAlphabetical))
      ) {
        update[prop] = newState[prop];
        continue;
      }
    }
  }

  if (Object.keys(update).length) return update;
  return null;
}

function CategoryEditDrawer(props) {
  const { category, categoryGroups, updateCategory, closeDrawer, classes } = props;

  const [update, setUpdate] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [state, dispatch] = useReducer(categoryReducer, initialState, () => {
    return {
      title: category?.title || '',
      description: category?.description || '',
      index: category?.index || '',
      coming_soon: category?.coming_soon || false,
      web_app_description: category?.web_app_description || '',
      category_groups: category?.category_groups || []
    };
  });

  useEffect(() => {
    if (category) setUpdate(getUpdate(state, category));
  }, [state, category]);

  function changeValue(e) {
    dispatch({ type: 'CHANGE_VALUE', payload: { [e.target.name]: e.target.value }});
  }
  function changeToggleValue(e) {
    dispatch({ type: 'CHANGE_VALUE', payload: { [e.target.name]: e.target.checked }});
  }

  function addGroup(group) {
    dispatch({ type: 'ADD_GROUP', payload: group });
  }

  function removeGroup(group) {
    dispatch({ type: 'REMOVE_GROUP', payload: group });
  }

  function submitUpdate() {
    if (update) {
      setUpdating(true);
      updateCategory(category._id, update);
    }
  }

  return (
    <>
      {category && <Container>
        {updating && <Loading><CircularProgress className={classes.progress} /></Loading>}
        <InputBox>
          <Label>Category ID</Label>
          <input type="number" value={category._id} disabled readOnly />
        </InputBox>
        <InputBox>
          <Label>Category Title</Label>
          <input type="text" value={state.title} name="title" onChange={changeValue} />
        </InputBox>
        <InputBox>
          <Label>Category Description</Label>
          <input type="text" value={state.description} name="description" onChange={changeValue} />
        </InputBox>
        <InputBox>
          <Label>Web App Short Description</Label>
          <input type="text" value={state.web_app_description} name="web_app_description" onChange={changeValue} />
        </InputBox>
        <InputBox>
          <Label>Category Groups</Label>
          <GroupsContainer>
            {categoryGroups.map(group => {
              const groupActive = state.category_groups.indexOf(group._id) > -1;
              return (
                <CategoryGroup
                  active={groupActive}
                  onClick={() => { groupActive ? removeGroup(group._id) : addGroup(group._id) }}
                  key={group._id}
                >
                  {group.title}
                </CategoryGroup>
              )
            })}
          </GroupsContainer>
        </InputBox>
        <InputBox>
          <Label>Category Index</Label>
          <input type="text" value={state.index} name="index" onChange={changeValue} />
        </InputBox>
        <InputBox>
          <Label>Coming soon</Label>
          <Switch
            name="coming_soon"
            value={state.coming_soon}
            checked={state.coming_soon}
            onChange={changeToggleValue}
          />
        </InputBox>
        <Label>Category Image</Label>
        <CategoryImage src={category.image_url} alt="category"/>
        <ButtonsContainer>
          <button
            className={update ? 'active' : ''}
            onClick={submitUpdate}>
            Save
          </button>
          <button className="active" onClick={closeDrawer}>Close</button>
        </ButtonsContainer>
      </Container>}
    </>
  );
}

export default withStyles(styles)(CategoryEditDrawer);

const Container = styled.div`
  position: relative;
  width: 40vw;
  min-width: 800px;
  padding: 2rem 3rem;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 1rem;
`;

const Loading = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(75, 75, 75, .2);
`

const InputBox = styled.div`
  width: 100%;

  && input {
    width: 100%;
    border: none;
    border-radius: 8px;
    padding: 0.75rem 1rem;
    background: #F9F9F9;
    box-sizing: border-box;
    -webkit-box-sizing:border-box;
    -moz-box-sizing: border-box;
  }
`;

const Label = styled.div`
  font-weight: 600;
  font-size: 1rem;
  color: #2F9D6D;
  margin-bottom: 0.5rem;
`;

const CategoryImage = styled.img`
  max-width: 50%;
  min-height: 260px;
  border: 1px solid #F4F4F4;
`;

const GroupsContainer = styled.div`
  display: flex;
  gap: 1rem;
`

const CategoryGroup = styled.span`
  display: inline-block;
  border-radius: 8px;
  cursor: pointer;
  background-color: ${props => props.active ? '#EFF9F1;' : '#F9F9F9'};
  padding: 16px 14px;
  border-width: 2px;
  border-style: solid;
  border-color: ${props => props.active ? '#4DB181' : 'transparent' };
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 1rem;

  && button {
    cursor: pointer;
    font-weight: 600;
    color: #FFFFFF;
    padding: 0.5rem 1rem;
    width: 138px;
    border-radius: 4px;
    border: 0;
    background: #C8C7CC;
  }

  && button.active {
    background: #4DB181;
  }
`;
